// LLM generated - Model Claude 4 Sonnet
/**
 * useContestChatActions - UI Action Handlers for Contest Chat
 *
 * This hook provides UI-interactive functions like:
 * - Reporting messages/posts
 * - Editing/deleting posts
 * - Muting users
 * - Chat administration (enable/disable)
 *
 * Note: Reaction handling and message sending are in useContestChatState
 * to keep optimistic updates co-located with state management.
 */
import { useState, useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { logError } from '~/common/utils/log';
import {
  updateChatAdministration,
  updateChatUserSettings,
  sendMessage as sendMessageAPI,
  editMessage,
  deleteMessage,
  flagMessage,
} from '~/services/live-activity/live-activity-service.service.api';
import type { Chat, GetChatsResponse, ReportReason } from '~/services/live-activity/types';
import { ContestChatModalContext } from '../ContestChatModals/ContestChatModalContext';
import { createQueryKey, MESSAGE_PAGE_SIZE } from '../constants/queryKeys';

type ChatUser = {
  id: string;
  handle?: string;
  avatarUrl?: string;
} | null;

type UseContestChatActionsProps = {
  contestId: string;
  messagesChat: Chat | undefined;
  postsChat: Chat | undefined;
  chatId?: string;
  activeTab: 'messages' | 'posts';
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  toggleChatAction?: (chatId: string, isEnabled: boolean) => void;
  user: ChatUser;
  /** Send post with optimistic updates - from useContestChatState */
  sendPostMessage?: (params: {
    message: string;
    header?: string;
    isPost?: boolean;
    notifyEntrants?: boolean;
  }) => Promise<boolean>;
};

export function useContestChatActions({
  contestId,
  messagesChat,
  postsChat,
  chatId,
  activeTab,
  scrollContainerRef,
  toggleChatAction,
  user,
  sendPostMessage,
}: UseContestChatActionsProps) {
  const { t } = useTranslation('chat');
  const { openModal, openModalUnique, closeModal } = useContext(ContestChatModalContext);
  const queryClient = useQueryClient();

  // Local state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localMuteStatus, setLocalMuteStatus] = useState<boolean | null>(null);
  const [isPostSubmitting, setIsPostSubmitting] = useState(false);

  // Memoize chat IDs
  const unidirectionalChatId = postsChat?.id;

  // Mutation for updating chat administration settings
  const updateChatMutation = useMutation({
    mutationFn: (payload: { isEnabled: boolean }) => {
      if (!chatId) throw new Error('Chat ID not available');
      return updateChatAdministration(chatId, payload);
    },
    onSuccess: (response) => {
      queryClient.setQueryData<GetChatsResponse>(['liveActivityChats', contestId], (oldData) => {
        if (!oldData || !oldData.data) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((chatItem) =>
            chatItem.id === response.data.id ? response.data : chatItem
          ),
        };
      });

      if (toggleChatAction && chatId) {
        toggleChatAction(chatId, response.data.isEnabled);
      }
      setIsModalOpen(false);
    },
    onError: (error) => {
      logError('Failed to update chat settings:', error);
    },
  });

  // Mutation for updating user settings (mute notifications)
  const updateUserSettingsMutation = useMutation({
    mutationFn: (payload: { muteNotifications: boolean }) => {
      if (!chatId) throw new Error('Chat ID not available');
      return updateChatUserSettings(chatId, payload);
    },
    onSuccess: (response) => {
      queryClient.setQueryData<GetChatsResponse>(['liveActivityChats', contestId], (oldData) => {
        if (!oldData || !oldData.data) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((chatItem) =>
            chatItem.id === chatId
              ? {
                  ...chatItem,
                  settings: {
                    ...chatItem.settings,
                    muteNotifications: response.muteNotifications,
                  },
                }
              : chatItem
          ),
        };
      });

      if (response.muteNotifications) {
        showNotification({
          message: t('notifications.chatMuted'),
          color: 'blue',
          autoClose: 5000,
        });
      }
    },
    onError: (error) => {
      logError('Failed to update user settings:', error);
    },
  });

  const postsQueryKey = createQueryKey.chatMessages(unidirectionalChatId, MESSAGE_PAGE_SIZE, false);

  // Mutation for sending posts via modal (commissioner posts with title)
  const sendPostMutation = useMutation({
    mutationFn: ({
      title,
      content,
      notifyEntrants,
    }: {
      title: string;
      content: string;
      notifyEntrants: boolean;
    }) => {
      if (!unidirectionalChatId) {
        throw new Error('No unidirectional chat found for posts');
      }

      return sendMessageAPI(
        unidirectionalChatId,
        {
          message: content,
          handle: user?.handle || 'Unknown',
          header: title,
          avatarUrl: user?.avatarUrl,
        },
        {
          notifyReaders: notifyEntrants,
        }
      ).then((result) => ({ ...result, notifyEntrants }));
    },
    onSuccess: (data) => {
      // Invalidate to fetch the new post from server
      queryClient.invalidateQueries({ queryKey: postsQueryKey });

      closeModal();

      const notificationMessage = data.notifyEntrants
        ? t('notifications.postSentSuccessNotify')
        : t('notifications.postSentSuccess');

      showNotification({
        title: notificationMessage,
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to send post:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.messageSendFailed'),
        color: 'red',
      });
    },
  });

  const editPostMutation = useMutation({
    mutationFn: ({
      messageId,
      chatId: editChatId,
      payload,
    }: {
      messageId: string;
      chatId: string;
      payload: { message: string; header?: string };
    }) => editMessage(editChatId, messageId, payload),
    onSuccess: () => {
      closeModal();
      showNotification({
        title: t('notifications.postUpdatedSuccess'),
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to edit post:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.postUpdateFailed'),
        color: 'red',
      });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: ({ messageId, chatId: deleteChatId }: { messageId: string; chatId: string }) =>
      deleteMessage(deleteChatId, messageId),
    onSuccess: () => {
      showNotification({
        title: t('notifications.postDeletedSuccess'),
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to delete post:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.postDeleteFailed'),
        color: 'red',
      });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: ({ messageId, chatId: deleteChatId }: { messageId: string; chatId: string }) =>
      deleteMessage(deleteChatId, messageId),
    onSuccess: () => {
      showNotification({
        title: t('notifications.messageDeletedSuccess'),
        message: '',
        color: 'green',
      });
    },
    onError: (error) => {
      logError('Failed to delete message:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.messageDeleteFailed'),
        color: 'red',
      });
    },
  });

  // Mutation for updating posts user settings
  const updatePostsUserSettingsMutation = useMutation({
    mutationFn: (payload: { muteNotifications: boolean; posterHandle?: string }) => {
      if (!unidirectionalChatId) throw new Error('No unidirectional chat found for muting');
      return updateChatUserSettings(unidirectionalChatId, {
        muteNotifications: payload.muteNotifications,
      });
    },
    onMutate: async (variables) => {
      setLocalMuteStatus(variables.muteNotifications);
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData<GetChatsResponse>(['liveActivityChats', contestId], (oldData) => {
        if (!oldData || !oldData.data) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((chatItem) =>
            chatItem.id === unidirectionalChatId
              ? {
                  ...chatItem,
                  settings: {
                    ...chatItem.settings,
                    muteNotifications: response.muteNotifications,
                  },
                }
              : chatItem
          ),
        };
      });

      const handle = variables.posterHandle || 'user';
      showNotification({
        title: variables.muteNotifications
          ? t('notifications.userMuted', { handle })
          : t('notifications.userUnmuted', { handle }),
        message: '',
        color: 'green',
      });
    },
    onError: (error, variables) => {
      setLocalMuteStatus(!variables.muteNotifications);
      logError('Failed to update posts mute settings:', error);
      showNotification({
        title: t('notifications.error'),
        message: t('notifications.postsMuteFailed'),
        color: 'red',
      });
    },
  });

  // Handler functions
  const handleOpenPostModal = useCallback(() => {
    openModalUnique({
      type: 'PostModal',
      onSubmit: async (title: string, content: string, notifyEntrants: boolean) => {
        closeModal();

        if (!sendPostMessage) {
          // Fallback to mutation if sendPostMessage not provided
          sendPostMutation.mutate({ title, content, notifyEntrants });
          return;
        }

        setIsPostSubmitting(true);
        const success = await sendPostMessage({
          message: content,
          header: title,
          notifyEntrants,
        });
        setIsPostSubmitting(false);

        if (success) {
          const notificationMessage = notifyEntrants
            ? t('notifications.postSentSuccessNotify')
            : t('notifications.postSentSuccess');
          showNotification({
            title: notificationMessage,
            message: '',
            color: 'green',
          });
        }
      },
      isSubmitting: isPostSubmitting || sendPostMutation.isLoading,
    });
  }, [openModalUnique, sendPostMessage, sendPostMutation, closeModal, t, isPostSubmitting]);

  const handleEditPost = useCallback(
    (messageId: string, editChatId: string, title: string, content: string) => {
      if (!unidirectionalChatId) {
        throw new Error('No unidirectional chat found for editing posts');
      }

      openModal({
        type: 'PostModal',
        isEditMode: true,
        initialTitle: title,
        initialContent: content,
        messageId,
        chatId: unidirectionalChatId,
        onSubmit: (updatedTitle: string, updatedContent: string, notifyEntrants: boolean) => {
          editPostMutation.mutate({
            messageId,
            chatId: unidirectionalChatId,
            payload: {
              message: updatedContent,
              header: updatedTitle,
            },
          });
        },
        isSubmitting: editPostMutation.isLoading,
      });
    },
    [unidirectionalChatId, openModal, editPostMutation]
  );

  const handleDeletePost = useCallback(
    (messageId: string, editChatId: string) => {
      if (!unidirectionalChatId) {
        throw new Error('No unidirectional chat found for deleting posts');
      }

      openModal({
        type: 'ConfirmModal',
        variant: 'delete',
        onConfirm: () => {
          deletePostMutation.mutate({
            messageId,
            chatId: unidirectionalChatId,
          });
        },
        isSubmitting: deletePostMutation.isLoading,
      });
    },
    [unidirectionalChatId, openModal, deletePostMutation]
  );

  const handleDeleteMessage = useCallback(
    (messageId: string) => {
      const bidirectionalChatId = messagesChat?.id;
      if (!bidirectionalChatId) {
        logError('No bidirectional chat found for deleting messages');
        return;
      }

      openModal({
        type: 'ConfirmModal',
        variant: 'deleteMessage',
        onConfirm: () => {
          deleteMessageMutation.mutate({
            messageId,
            chatId: bidirectionalChatId,
          });
        },
        isSubmitting: deleteMessageMutation.isLoading,
      });
    },
    [messagesChat?.id, openModal, deleteMessageMutation]
  );

  const handleMutePost = useCallback(
    (posterHandle: string) => {
      const currentMuteStatus = localMuteStatus ?? false;

      if (!currentMuteStatus) {
        openModal({
          type: 'ConfirmModal',
          variant: 'mute',
          title: t('mutePost.title', { handle: posterHandle }),
          description: t('mutePost.description', { handle: posterHandle }),
          onConfirm: () => {
            updatePostsUserSettingsMutation.mutate({
              muteNotifications: true,
              posterHandle,
            });
          },
          isSubmitting: updatePostsUserSettingsMutation.isLoading,
        });
      } else {
        updatePostsUserSettingsMutation.mutate({
          muteNotifications: false,
          posterHandle,
        });
      }
    },
    [localMuteStatus, openModal, t, updatePostsUserSettingsMutation]
  );

  const handleReportClick = useCallback(
    (handle: string, messageId: string) => {
      const contentType = activeTab === 'posts' ? 'post' : 'message';
      const reportChatId = activeTab === 'posts' ? postsChat?.id : messagesChat?.id;

      if (!reportChatId) {
        logError('No chat ID available for reporting');
        return;
      }

      openModal({
        type: 'ReportModal',
        contentType,
        contentId: messageId,
        reportedHandle: handle,
        chatId: reportChatId,
        messageId,
        onSubmit: (reason: string, description?: string) => {
          flagMessage(reportChatId, messageId, { reason: reason as ReportReason, description });
        },
      });
    },
    [activeTab, postsChat, messagesChat, openModal]
  );

  const handleTabChange = useCallback(
    (tab: 'messages' | 'posts') => {
      setTimeout(() => {
        const scrollElement = scrollContainerRef?.current as HTMLDivElement | null;
        if (scrollElement) {
          if (tab === 'posts') {
            scrollElement.scrollTop = 0;
          } else {
            scrollElement.scrollTop = scrollElement.scrollHeight;
          }
        }
      }, 100);
    },
    [scrollContainerRef]
  );

  const handleNotificationClick = useCallback(
    (currentMuteNotifications: boolean, isCommissioner: boolean) => {
      if (isCommissioner) {
        setIsModalOpen(true);
      } else {
        updateUserSettingsMutation.mutate({ muteNotifications: !currentMuteNotifications });
      }
    },
    [updateUserSettingsMutation]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalConfirm = useCallback(
    (chatEnabledFromAPI: boolean) => {
      updateChatMutation.mutate({ isEnabled: !chatEnabledFromAPI });
    },
    [updateChatMutation]
  );

  return {
    // State
    isModalOpen,
    localMuteStatus,
    setLocalMuteStatus,

    // Mutations
    sendPostMutation,
    updateChatMutation,
    updateUserSettingsMutation,

    // Handlers
    handleOpenPostModal,
    handleEditPost,
    handleDeletePost,
    handleDeleteMessage,
    handleMutePost,
    handleReportClick,
    handleTabChange,
    handleNotificationClick,
    handleModalClose,
    handleModalConfirm,
  };
}
